import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocale } from "../../contexts/localeContext";
import { Lookup } from "../../models/Lookup";
import { toast } from "react-toastify";
import LookupTypes from "../../models/LookupTypes";
import { getSiteNamesWithPagination } from "../../services/siteService";
import SiteSearch from "../../models/search/SiteSearch";
import { getEventTypeNamesWithPagination } from "../../services/eventTypeService";
import EventTypeSearch from "../../models/search/EventTypeSearch";
import { getCompanyNamesWithPagination } from "../../services/companyService";
import CompanySearch from "../../models/search/CompanySearch";
import { getDepartmentNamesWithPagination } from "../../services/departmentService";
import DepartmentSearch from "../../models/search/DepartmentSearch";
import { getDoorNamesWithPagination } from "../../services/doorService";
import DoorSearch from "../../models/search/DoorSearch";
import { getZoneNamesWithPagination } from "../../services/zoneService";
import BuildingSearch from "../../models/search/BuildingSearch";
import { getControllerNamesWithPagnation } from "../../services/controllerService";
import { getReaderNamesWithPagination } from "../../services/readerService";
import ZoneSearch from "../../models/search/ZoneSearch";
import { getBuildingNamesWithPagination } from "../../services/buildingService";
import ControllerSearch from "../../models/search/ControllerSearch";
import ReaderSearch from "../../models/search/ReaderSearch";
import { ListQuery } from "../../models/ListQuery";
import { REPORTS_TRANSACTION } from "../../constants/menu-types";
import { Filter } from "../../models/Filter";
import { getUserFiltersByView } from "../../services/userPreferenceService";
import { getTodayDate } from "../../utilities/DateUtils";
import { showError } from "../../services/utilities/ServiceError";
import { TableColumn } from "../../models/table/TableColumn";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import TableTab from "../../components/tabs/table-tab";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Button } from "../../components/button/Button";
import { ListData } from "../../models/ListData";
import { FILTER_DATE } from "../../constants/filter-types";
import { TransactionReportWrapper } from "../../models/TransactionReport";
import { DEF_PG_SIZE_SUB_MIN } from "../../constants/maximum-page-size";
import UserTransactionColumn from "../../models/ColumnDefinitions/UserTransactionsColumn";
import UserTransactionSearch from "../../models/search/UserTransactionsSearch";
import OssDeviceSearch from "@/models/search/OssDeviceSearch";
import { getOssDeviceNamesWithPagination } from "@/services/ossDeviceService";

interface Props {
    isTodayReportType: boolean;
    masterId: string;
    dataMethod:(listParameters?: ListQuery) => Promise<TransactionReportWrapper>
    dataReloadMethod :() => void
}

const UserTransactions: FC<Props> = ({ isTodayReportType, masterId,dataMethod,dataReloadMethod}) => {
    const { translate } = useLocale();
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [refreshReport, setRefreshReport] = useState(true);

    useEffect(() => {
        setLookup([
            {
                name: LookupTypes.zoneBySiteId,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.eventType,
                values: [],
                dataMethod: getEventTypeNamesWithPagination,
                searchAccessor: EventTypeSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.location,
                values: [],
                dataMethod: getDoorNamesWithPagination,
                searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.zone,
                values: [],
                dataMethod: getZoneNamesWithPagination,
                searchAccessor: ZoneSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.building,
                values: [],
                dataMethod: getBuildingNamesWithPagination,
                searchAccessor: BuildingSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.controller,
                values: [],
                dataMethod: getControllerNamesWithPagnation,
                searchAccessor: ControllerSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.terminal,
                values: [],
                dataMethod: getReaderNamesWithPagination,
                searchAccessor: ReaderSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.ossDevice,
                values: [],
                dataMethod: getOssDeviceNamesWithPagination,
                searchAccessor: OssDeviceSearch.find(item => item.accessor === "name")?.searchOnName
            },
        ]);
    }, [])

    useEffect(() => {
        if (refreshReport) {
            dataReloadMethod();
            dataRefresh(dataMethod);
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_TRANSACTION;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter) => void) => {

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail).then((response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter = [{
                    isCustomFilter: true,
                    key: "dateOnly",
                    label: "Date",
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                    }]
                }];
            }
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter);
        })
    }
    // <-- end context

    const getReportColumn = (): TableColumn[] => {
        if (isTodayReportType) {
            return UserTransactionColumn.filter((column: TableColumn) => { return column.accessor !== 'dateOnly' });
        }
        return UserTransactionColumn;
    }
   
    useEffect(() =>{
        dataRefresh(dataMethod);
    },[masterId])

    return (<TableContextProvider
        dataMethod={dataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={getReportColumn()}
        searchDescriptor={UserTransactionSearch}
        useFilter
        useSort
        usePagination
        useSearch
        maxPageSize={DEF_PG_SIZE_SUB_MIN}
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.transactions)}
            selectRows={false}
            showFilterMenu={isTodayReportType ? false : true}
            disableFilterToggling ={true}
            cyName="userAudit"
            >
            <Button variant="submit" shape="rounded" className="py-1 px-5 ml-8" onClick={() => setRefreshReport(true)}>
                    {translate.byKey(TranslationKeys.refresh)}
                </Button>
        </TableTab>
    </TableContextProvider >
    );
};

export default UserTransactions;
